import { OrderedMap } from 'immutable';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton/IconButton';
import Typography from '@mui/material/Typography';
import { bulkWritesActions } from 'app-components/flux/bulk-writes';
import { undoActions } from 'app-components/flux/undo';

import store from 'app-components/utils/redux-store';

import * as notificationsActions from './notificationsActions';
import { mkNotification } from './notificationsTypes';

export const dispatchSimpleNotification = (message, props) => {
  const id = uuidv4();
  store.dispatch(notificationsActions.addNotifications(OrderedMap([[id, mkNotification({ id, message, ...props })]])));
};

export const notificationFromUndo = (undo) =>
  mkNotification({
    id: 'toast-message', //  undo.key,
    message: undo.userMessage,
    createdAt: undo.createdAt,
    anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
    snackbarContentProps: {
      action: [
        ...(!undo.actions?.length
          ? []
          : [
              <Button
                id='undo-button'
                key='undo'
                aria-label='undo'
                color='inherit'
                onClick={() => store.dispatch(undoActions.undo(undo))}
              >
                Undo
              </Button>,
            ]),
        <IconButton
          id='close-undo'
          key='close'
          aria-label='Close'
          onClick={() => store.dispatch(undoActions.deleteUndo(undo))}
          size='large'
          color='inherit'
        >
          <CloseIcon />
        </IconButton>,
      ],
      onClose: (event, reason) => {
        if (reason !== 'clickaway') {
          store.dispatch(undoActions.deleteUndo(undo));
        }
      },
    },
  });

// Advice specific to the surface, so it lives here rather than in the shared resource config. Only
// resources that opt into splitting can reach this, and one without copy still gets the count line.
export const BULK_WRITE_DETAIL_MESSAGES = {
  transaction: 'Keep this window open and avoid making other transaction updates.',
};

const twoLineContent = (icon, headline, detail) => (
  <Box>
    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
      {icon}
      {headline}
    </Box>
    {detail ? (
      <Typography variant='body2' color='inherit' sx={{ opacity: 0.7, paddingLeft: 3 }}>
        {detail}
      </Typography>
    ) : null}
  </Box>
);

// A failed bulk write is reported by whatever the operation itself was, but only generically ("we
// were unable to make your changes"). What that can't say, and what matters after a split write, is
// how much of the payload landed before it stopped.
const failedBulkWriteNotification = (bulkWrite) => {
  const { id, resourceName, total } = bulkWrite;
  const { committed, retryQueued } = bulkWrite.failure;
  const dismiss = () => store.dispatch(bulkWritesActions.bulkWriteFinished(id));
  const message = `Couldn't finish updating ${total.toLocaleString()} ${resourceName}s`;
  // A floor, not an exact count, so it is worded as one: the request that failed may have written
  // some of its own resources before erroring. Nothing replays a queued retry on its own, so that
  // case points at the Try again control rather than implying the write is still going.
  const detail = [
    committed > 0 ? `At least ${committed.toLocaleString()} were updated.` : null,
    retryQueued ? 'Use Try again to retry the update.' : `Check your ${resourceName}s before trying again.`,
  ]
    .filter(Boolean)
    .join(' ');

  return {
    message,
    // Persistent: the count is what the user needs in order to decide whether to retry, and the
    // retry snackbar outlives a 10s toast.
    autoHideDuration: null,
    snackbarContentProps: {
      message: twoLineContent(<ErrorOutlineIcon fontSize='small' />, message, detail),
      action: (
        <IconButton id='close-bulk-write' aria-label='Close' onClick={dismiss} size='large' color='inherit'>
          <CloseIcon />
        </IconButton>
      ),
      // The record lives in the bulk-writes store, not the notifications store, so the default
      // removal would be a no-op and the toast would never close — including on auto-hide.
      onClose: (event, reason) => {
        if (reason !== 'clickaway') {
          dismiss();
        }
      },
    },
  };
};

const pendingBulkWriteNotification = (bulkWrite) => {
  const { resourceName, submitted, total } = bulkWrite;
  const message = `Updating ${submitted.toLocaleString()} of ${total.toLocaleString()} ${resourceName}s\u2026`;

  return {
    message,
    // Persistent: it is replaced by the undo toast on success and by the failure state otherwise.
    autoHideDuration: null,
    snackbarContentProps: {
      message: twoLineContent(
        <CircularProgress size={16} color='inherit' />,
        message,
        BULK_WRITE_DETAIL_MESSAGES[resourceName],
      ),
      // The operation isn't in the notifications store, so the default dismiss button would fire a
      // removal that does nothing; it clears itself when the last request lands.
      action: null,
    },
  };
};

// A resource write large enough to be split into several requests takes tens of seconds, during
// which the undo toast is deliberately held back, so this is the only sign anything is happening.
export const notificationFromBulkWrite = (bulkWrite) =>
  mkNotification({
    // Distinct from the undo toast's fixed 'toast-message' id, which the notification map would
    // otherwise collapse this into.
    id: `bulk-progress-${bulkWrite.id}`,
    createdAt: undefined,
    anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
    ...(bulkWrite.failure ? failedBulkWriteNotification(bulkWrite) : pendingBulkWriteNotification(bulkWrite)),
  });
